import { getTheme, IconButton, IStyle, ITheme, mergeStyleSets, Modal } from "@fluentui/react";
import { IProductTexture } from "../../../entities-generated/common/product-texture";
import { IProductDetailDialogDisplayProps, ProductDetailDialogContent } from "./content";
import { IBrowserImage } from "../../../framework/components/bwj-image-browser";
import { useCallback, useMemo, useState } from "react";
import { fontFamily } from "../../../app-core/app-theme";
import { noWrapStyle } from "../../../framework/utils/no-wrap-style";
import { distinct, forEach } from "../../../framework/utils/collection";
import { BwjSemiFlexContainer } from "../../../framework/components/bwj-semi-flex-container";
import { BwjScrollContainer } from "../../../framework/components/bwj-scroll-container";
import { BwjConscientiousContainer } from "../../../framework/components/bwj-conscientious-container";
import { isTouchDevice } from "../../../framework/utils/device";


interface IProductDetailDialogProps extends IProductDetailDialogDisplayProps {
  showDialog: boolean;
  imageUrls: string[];
  onCloseDialog: () => void;
}

const getStyleClasses = (theme: ITheme) => {

  const main: IStyle = {
    width: 'calc(100% - 3rem)',
    maxWidth: '75rem',
    height: 'calc(100vh - 10rem)',
  };
  const msScrollableContent: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };
  const root: IStyle = {
    fontFamily: fontFamily,

    '& .ms-Dialog-main': main,
    '& .ms-Modal-scrollableContent': msScrollableContent,
  };

  const header: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: theme.fonts.xSmall.fontSize,
    alignItems: 'center',

    '& > h1': {
      ...noWrapStyle as any,
      fontSize: theme.fonts.xLarge.fontSize,
      lineHeight: theme.fonts.xLarge.lineHeight,
      fontWeight: 500,
      margin: 0,
      padding: 0,
      width: 1,
      flexGrow: 1,
    }
  };

  const scrollBody: IStyle = {
    padding: '0 1rem',
    paddingTop: '1rem',
    paddingBottom: 96,
  };

  const contentContainer: IStyle = {
    maxWidth: 1140,
    minWidth: 300,
    margin: '0 auto',
  };

  return mergeStyleSets({
    root,
    header,
    scrollBody,
    contentContainer,
  });
}

const getCloseButtonStyles = (theme: ITheme) => {
  return {
    root: {
      color: theme.palette.neutralPrimary,
      marginLeft: 'auto',
      marginTop: '4px',
      marginRight: '2px',
    },
    rootHovered: {
      color: theme.palette.neutralDark,
    },
  };
}

const touchDevice = isTouchDevice();

export const ProductDetailDialog: React.FunctionComponent<IProductDetailDialogProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const closeButtonStyles = getCloseButtonStyles(theme);
  const { title, imageUrls } = props;
  const [selectedVariant, setSelectedVariant] = useState<IProductTexture | null>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [displayedImageIndex, setDisplayedImageIndex] = useState(0);
  const [showMeasurementRequestForm, setShowMeasurementRequestForm] = useState(false);
  const images = useMemo(() => {
    const imgs: IBrowserImage[] = !!selectedVariant ? selectedVariant.productImageUrls.map(src => {
      return { src, alt: title };
    }) : [];

    forEach(imageUrls, src => {
      imgs.push({ src, alt: title });
    });

    const uniqueImgs = distinct(imgs, i => i.src);
    return uniqueImgs;
  }, [imageUrls, selectedVariant, title]);

  const onWidthChange = useCallback((width: number) => setContentWidth(width), []);

  const onVariantSelected = (variant: IProductTexture) => {
    setSelectedVariant(variant);
  };

  const onMeasurementRequestSubmitted = () => {
    setShowMeasurementRequestForm(false);
    props.onMeasurementRequestSubmitted();
  };

  return (
    <Modal
      isOpen={props.showDialog}
      isBlocking={true}
      onDismiss={props.onCloseDialog}
      className={cs.root}
      allowTouchBodyScroll={touchDevice}
    >
      <div className={cs.header}>
        <h1>{'Storm Door'}</h1>
        <IconButton
          styles={closeButtonStyles}
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel='Close product detail dialog'
          onClick={props.onCloseDialog}
        />
      </div>
      <BwjSemiFlexContainer
        pinHeight={true}
        styles={{
          root: { height: 1, flexGrow: 1, width: '100%' },
          content: {},
        }}
      >
        <BwjScrollContainer>
          <div className={cs.scrollBody}>
            <BwjConscientiousContainer
              className={cs.contentContainer}
              pollingFrequency={1200}
              onWidthChange={onWidthChange}
            >
              <ProductDetailDialogContent
                id={props.id}
                brand={props.brand}
                title={title}
                averageRating={props.averageRating}
                rating={props.rating}
                keyFeatures={props.keyFeatures}
                imageUrls={images}
                displayedImageIndex={displayedImageIndex}
                onDisplayedIndexChanged={setDisplayedImageIndex}
                variants={props.variants}
                variantDescriptor={props.variantDescriptor}
                selectedVariant={selectedVariant}
                onVariantSelected={onVariantSelected}
                measurementRequestDate={props.measurementRequestDate}
                requestMeasurementFormModel={props.requestMeasurementFormModel}
                showMeasurementRequestForm={showMeasurementRequestForm}
                onToggleMeasurementRequestForm={setShowMeasurementRequestForm}
                onMeasurementRequestSubmitted={onMeasurementRequestSubmitted}
                onShowPricingExplanation={props.onShowPricingExplanation}
                contentWidth={contentWidth}
              />
            </BwjConscientiousContainer>
          </div>
        </BwjScrollContainer>
      </BwjSemiFlexContainer>
    </Modal>
  );
};